import { AlertCircle, CheckCircle2, Shield, FileText, CreditCard, UserCheck, ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";
import { grayColor } from "Common/commaon";
import { useNavigate } from "react-router-dom";

interface KYCPendingViewProps {
  onBack?: () => void;
  onContinueKYC?: () => void;
}

export function KYCPendingView({ onBack, onContinueKYC }: KYCPendingViewProps) {
  // Mock data - in real app this would come from your backend
  const currentStep = 1;
  const totalSteps = 4;
  const progressPercentage = (currentStep / totalSteps) * 100;
const navigate = useNavigate()
  const handleContinueKYC = () => {
  navigate("/kyc-form-page")
  };

  const steps = [
    {
      icon: Shield,
      title: "Verify Your Identity",
      description: "Provide your personal information and upload a government-issued ID (driver's license, passport, etc.)",
      status: "pending",
      estimatedTime: "2-3 minutes"
    },
    {
      icon: FileText,
      title: "Business Information",
      description: "Enter your business details including address and business type",
      status: "incomplete",
      estimatedTime: "1-2 minutes"
    },
    {
      icon: CreditCard,
      title: "Banking Details",
      description: "Link your bank account where you'll receive milestone payments",
      status: "incomplete",
      estimatedTime: "2-3 minutes"
    },
    {
      icon: UserCheck,
      title: "Review & Submit",
      description: "Review all information and submit for verification",
      status: "incomplete",
      estimatedTime: "1 minute"
    }
  ];

  return (
    <div className="tailwind-prefix page-content">
    <div className="container max-w-4xl mx-auto px-4  ">
      {/* Back Button */}
      {onBack && (
        <Button variant="ghost" className="mb-4" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Payment
        </Button>
      )}
      
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
          <AlertCircle className="w-8 h-8 text-amber-600" />
        </div>
        <div className="mb-2 text-[15px]  ">KYC Verification Pending</div>
        <p className="text-slate-600 max-w-2xl mx-auto">
          To receive milestone payments from contractors, please complete your Stripe KYC verification. This ensures secure and compliant transactions.
        </p>
      </div>

      {/* Status Alert */}
      <Alert className="mb-6 border-amber-200 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-900">
          Your account is currently pending verification. You won't be able to receive payments until KYC is complete.
        </AlertDescription>
      </Alert>

      {/* Progress Card */}
      <Card className="p-3 mb-6 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="mb-1">Verification Progress</div>
            <div className="text-sm text-slate-600">Complete all steps to start receiving payments</div>
          </div>
          <Badge variant="secondary" className="text-slate-700 bg-gray-200">
            {currentStep} of {totalSteps}
          </Badge>
        </div>
        <Progress value={progressPercentage} className="h-2 bg-gray-200 " />
        <div className="text-sm text-slate-600 mt-2">Estimated time remaining: 6-9 minutes</div>
      </Card>

      {/* Steps */}
      <Card className="p-6 mb-6 bg-white">
        <div className="mb-6 text-[14px]">Complete Your Verification</div>
        
        <div className="space-y-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isPending = step.status === "pending";
            const isComplete = step.status === "complete";
            
            return (
              <div key={index}>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isPending 
                        ? "bg-blue-100" 
                        : isComplete 
                        ? "bg-green-100" 
                        : "bg-slate-100"
                    }`}>
                      {isComplete ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      ) : (
                        <Icon className={`w-6 h-6 ${
                          isPending ? "text-blue-600" : "text-slate-400"
                        }`} />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-[14px]">{step.title}</div>
                      {isPending && (
                        <Badge variant="default" className="bg-blue-600 text-white">Current Step</Badge>
                      )}
                      {isComplete && (
                        <Badge variant="default" className="bg-green-600">Complete</Badge>
                      )}
                    </div>
                    <p className="text-slate-600 mb-2">{step.description}</p>
                    <div className="text-sm text-slate-500">⏱️ {step.estimatedTime}</div>
                  </div>
                </div>
                
                {index < steps.length - 1 && (
                  <div style={{
                    borderTop:`1px solid ${grayColor}`
                  }} className="my-6 " />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button  size="lg" className="bg-blue-600 border-0 text-white hover:bg-blue-700" onClick={handleContinueKYC}>
          Continue KYC Verification
        </Button>
        <Button size="lg" className="bg-white" variant="outline">
          Need Help?
        </Button>
      </div>

      {/* Additional Info */}
      <Card className="p-6 mt-6 bg-slate-50 border-slate-200">
        <div className="">Why do we need this?</div>
        <ul className="space-y-2 text-slate-600">
          <li className="flex gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span><strong>Legal Compliance:</strong> KYC verification is required by financial regulations</span>
          </li>
          <li className="flex gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span><strong>Secure Payments:</strong> Protects both contractors and homeowners from fraud</span>
          </li>
          <li className="flex gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span><strong>Fast Processing:</strong> Once verified, receive milestone payments instantly</span>
          </li>
        </ul>
      </Card>

      {/* Footer Note */}
      <p className="text-center text-sm text-slate-500 mt-8">
        Your information is securely processed by Stripe and encrypted. We never store your sensitive data.
      </p>
    </div>
    </div>

  );
}
